import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { readRedirectFromQuery } from '../utils/redirectAfterLogin';

export default function Register() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { register, resendConfirmationCode, loginWithGoogle } = useAuth();
  // Where to land after sign-up (e.g. back on the page where "save" was tapped).
  const redirectTo = readRedirectFromQuery();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.name === 'email' ? e.target.value.toLowerCase() : e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate name is provided
    if (!formData.name.trim()) {
      setError(t('register.nameRequired', 'Please enter your name'));
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError(t('register.passwordMismatch', 'Passwords do not match'));
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setError(t('register.passwordTooShort', 'Password must be at least 8 characters long'));
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name);

      // Navigate to confirmation page
      navigate('/confirm-email', {
        state: { email: formData.email, redirect: redirectTo },
      });
    } catch (err: unknown) {
      const errorName = err instanceof Error ? err.name : '';
      const errorMessage =
        err instanceof Error
          ? err.message
          : t('register.registrationFailed', 'Registration failed. Please try again.');

      // Handle UsernameExistsException: resend verification code and continue registration
      if (errorName === 'UsernameExistsException') {
        try {
          await resendConfirmationCode(formData.email);
          // User exists but not confirmed — go to confirm page
          navigate('/confirm-email', {
            state: { email: formData.email, isRecovery: true, redirect: redirectTo },
          });
          return;
        } catch (resendErr: unknown) {
          const resendErrorName = resendErr instanceof Error ? resendErr.name : '';
          // InvalidParameterException = user already confirmed their email
          if (resendErrorName === 'InvalidParameterException') {
            setError(
              t(
                'register.accountExists',
                'This email is already registered. Please sign in or reset your password.'
              )
            );
          } else {
            // Temporary failure (network, throttle, etc.) — show generic error
            const resendMessage =
              resendErr instanceof Error
                ? resendErr.message
                : t('register.registrationFailed', 'Registration failed. Please try again.');
            setError(resendMessage);
          }
          return;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setError('');
      await loginWithGoogle(redirectTo ?? undefined);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : t('register.googleFailed', 'Google sign up failed');
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Card Container */}
        <div className="bg-white rounded-xl shadow-brand p-8 space-y-6">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-h2 font-bold text-charcoal">
              {t('register.title', 'Create your account')}
            </h2>
            <p className="mt-3 text-body text-charcoal/70">
              {t('register.haveAccount', 'Already have an account?')}{' '}
              <Link
                to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'}
                className="font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                {t('register.signIn', 'Sign in')}
              </Link>
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            )}

            {/* Name Input */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-2">
                {t('register.name', 'Name')} <span className="text-coral-orange">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder={t('register.namePlaceholder', 'Enter your name')}
              />
            </div>

            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-2">
                {t('register.email', 'Email address')} <span className="text-coral-orange">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder={t('register.emailPlaceholder', 'you@example.com')}
              />
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-charcoal mb-2">
                {t('register.password', 'Password')} <span className="text-coral-orange">*</span>
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder={t('register.passwordPlaceholder', '••••••••')}
              />
            </div>

            {/* Confirm Password Input */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-charcoal mb-2"
              >
                {t('register.confirmPassword', 'Confirm Password')}{' '}
                <span className="text-coral-orange">*</span>
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder={t('register.confirmPasswordPlaceholder', 'Re-enter password')}
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-primary-pale rounded-lg p-4">
              <p className="text-xs font-medium text-charcoal mb-2">
                {t('register.passwordRequirements', 'Password must contain:')}
              </p>
              <ul className="text-xs text-charcoal/70 space-y-1">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>{t('register.req1', 'At least 8 characters')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>{t('register.req2', 'At least one uppercase letter')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>{t('register.req3', 'At least one lowercase letter')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>{t('register.req4', 'At least one number')}</span>
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-6 border border-transparent text-body font-semibold rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? t('register.creating', 'Creating account...')
                : t('register.createAccount', 'Create account')}
            </button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-charcoal/60">
                {t('login.orContinueWith', 'Or continue with')}
              </span>
            </div>
          </div>

          {/* Google Sign Up Button */}
          <button
            type="button"
            onClick={handleGoogleSignUp}
            className="w-full flex justify-center items-center py-3 px-6 border border-soft-gray rounded-lg text-charcoal bg-white hover:bg-warm-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('login.continueWithGoogle', 'Continue with Google')}
          </button>
        </div>
      </div>
    </div>
  );
}
